export const API_BASE = process.env.VUE_APP_KENDOU_API_BASE || "";

function looksLikeHtml(value) {
  return typeof value === "string" && /<\s*(!doctype|html|head|body)\b/i.test(value);
}

export function ensureApiObject(response, label = "API") {
  const data = response?.data;

  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data;
  }

  if (looksLikeHtml(data)) {
    throw new Error(`${label} returned HTML instead of JSON. Check VUE_APP_KENDOU_API_BASE and the deployed backend.`);
  }

  throw new Error(`${label} returned an unexpected payload.`);
}

export function ensureApiArray(response, label = "API") {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (looksLikeHtml(data)) {
    throw new Error(`${label} returned HTML instead of JSON. Check VUE_APP_KENDOU_API_BASE and the deployed backend.`);
  }

  throw new Error(`${label} returned an unexpected payload.`);
}

const STATUS_LABELS = {
  ja: {
    active: "公開中",
    draft: "下書き",
    completed: "完結",
    archived: "保管",
    scheduled: "予約",
    published: "公開済み",
  },
  zh: {
    active: "进行中",
    draft: "草稿",
    completed: "已完结",
    archived: "归档",
    scheduled: "预约",
    published: "已发布",
  },
  en: {
    active: "Active",
    draft: "Draft",
    completed: "Completed",
    archived: "Archived",
    scheduled: "Scheduled",
    published: "Published",
  },
};

const CATEGORY_LABELS = {
  ja: {
    history: "歴史",
    kamae: "構え",
    legacy_archive: "来歴",
    legacy_episode: "原点記録",
    ryuha: "流派",
    essay: "雑談",
    misc: "その他",
    waza: "技",
    footwork: "足さばき",
    armor_setup: "着装",
    practice_method: "稽古法",
  },
  zh: {
    history: "历史",
    kamae: "构え",
    legacy_archive: "来时路",
    legacy_episode: "旧视频",
    ryuha: "流派",
    essay: "杂谈",
    misc: "其他",
    waza: "技术",
    footwork: "步法",
    armor_setup: "着装",
    practice_method: "练习法",
  },
  en: {
    history: "History",
    kamae: "Kamae",
    legacy_archive: "Archive",
    legacy_episode: "Legacy",
    ryuha: "School",
    essay: "Essay",
    misc: "Misc",
    waza: "Waza",
    footwork: "Footwork",
    armor_setup: "Equipment",
    practice_method: "Practice",
  },
};

const RELATION_LABELS = {
  ja: {
    explains: "説明",
    uses: "使用",
    requires: "前提",
    related_to: "関連",
    component_of: "構成",
    example_of: "例示",
    mentions: "言及",
    focuses_on: "主題",
    background_of: "背景",
  },
  zh: {
    explains: "说明",
    uses: "使用",
    requires: "前提",
    related_to: "关联",
    component_of: "组成",
    example_of: "例子",
    mentions: "提及",
    focuses_on: "主题",
    background_of: "背景",
  },
  en: {
    explains: "Explains",
    uses: "Uses",
    requires: "Requires",
    related_to: "Related",
    component_of: "Component",
    example_of: "Example",
    mentions: "Mentions",
    focuses_on: "Focus",
    background_of: "Background",
  },
};

export function pickLangText(value, lang, fallback = "-") {
  if (!value) {
    return fallback;
  }

  if (typeof value === "string") {
    return value.trim() || fallback;
  }

  const keys = [lang, "ja", "zh", "en", "jp", "ch"];
  for (const key of keys) {
    const text = value[key];
    if (typeof text === "string" && text.trim()) {
      return text.trim();
    }
  }

  const firstText = Object.values(value).find((item) => typeof item === "string" && item.trim());
  return firstText || fallback;
}

export function summarizeText(value, lang, maxLength = 120) {
  const text = pickLangText(value, lang, "");
  if (text.length <= maxLength) {
    return text || "-";
  }

  return `${text.slice(0, maxLength).trim()}...`;
}

export function getStatusLabel(status, lang) {
  return STATUS_LABELS[lang]?.[status] || status || "-";
}

export function getCategoryLabel(category, lang) {
  return CATEGORY_LABELS[lang]?.[category] || category || "-";
}

export function getRelationLabel(relationType, lang) {
  return RELATION_LABELS[lang]?.[relationType] || relationType || "-";
}

export function formatDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function formatDateOnly(value) {
  if (!value) {
    return "-";
  }

  if (typeof value === "string") {
    return value;
  }

  return `${value.year}-${String(value.month).padStart(2, "0")}-${String(value.day).padStart(2, "0")}`;
}

export function toArray(value) {
  return Array.isArray(value) ? value : [];
}

export function extractMediaIds(value, lang) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string") {
    return value ? [value] : [];
  }

  if (typeof value !== "object") {
    return [];
  }

  const tryKeys = [`id_${lang}`, "id_ja", "id_zh", "id_en", "ja", "zh", "en"];
  const collected = [];

  for (const key of tryKeys) {
    const entry = value[key];
    if (typeof entry === "string" && entry.trim()) {
      collected.push(entry.trim());
    } else if (Array.isArray(entry)) {
      collected.push(...entry.filter(Boolean));
    }
  }

  if (Array.isArray(value.items)) {
    for (const item of value.items) {
      if (!item || typeof item !== "object") {
        continue;
      }

      for (const key of tryKeys) {
        const entry = item[key];
        if (typeof entry === "string" && entry.trim()) {
          collected.push(entry.trim());
        } else if (Array.isArray(entry)) {
          collected.push(...entry.filter(Boolean));
        }
      }
    }
  }

  return [...new Set(collected)];
}

export function youtubeEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}`;
}

export function bilibiliEmbedUrl(videoId) {
  return `https://player.bilibili.com/player.html?bvid=${videoId}&autoplay=0`;
}

